import React, { useMemo } from "react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { AlertTriangle, MessageSquare, Clock, FileText, ListTodo } from "lucide-react";

/**
 * SidebarBadgePopover — panneau flottant qui apparaît au survol des
 * icônes sidebar (AVI, Documents, Tâches). Post-prod rev-4 point 4.
 * Spec iter171 pt.3 (2026-02-27) — refonte du style :
 *   • Titre clair en haut (« Notifications tâches / documents / … »).
 *   • Bloc contenu avec BORDURE GAUCHE 3 px dans la couleur du module.
 *   • Référence (ex. TH0001/26) affichée en PASTILLE PLEINE colorée
 *     dans la couleur du module, CLIQUABLE — un clic ouvre la fiche.
 *   • En dessous : nom/objet en gras, puis statut (icône + date) en meta.
 *
 * Le composant reçoit une `moduleColor` (hex) fournie par le parent
 * (Sidebar) — dérivée du module courant.
 */
const PRIORITY_COLOR = {
  immediat: "#EF4444", urgent: "#F97316", moyen: "#F9BC26", normal: "#4ADE80",
  immediate: "#EF4444", high: "#F97316", medium: "#F9BC26", low: "#4ADE80",
};

export default function SidebarBadgePopover({ visible, sections = [], title = "Détails", moduleColor = "#F87171" }) {
  const nav = useNavigate();
  const empty = useMemo(
    () => sections.every((s) => (s.items || []).length === 0),
    [sections],
  );
  if (!visible) return null;
  return (
    <div className="absolute left-full top-0 pl-3" style={{ zIndex: 9999 }}>
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: "#161616",
        border: "1px solid #2A2A2A",
        boxShadow: "0 12px 28px rgba(0,0,0,0.55)",
        width: 380,
        maxHeight: 480,
      }}
      data-testid="sidebar-badge-popover"
    >
      <div
        className="px-4 py-2.5"
        style={{
          borderBottom: "1px solid #2A2A2A",
          // Spec iter171 pt.3 (2026-02-27) — bandeau titre coloré, texte clair.
          background: `${moduleColor}14`,
        }}
      >
        <div
          className="text-[11px] uppercase tracking-widest font-semibold"
          style={{ color: moduleColor }}
        >
          {title}
        </div>
      </div>
      <div
        className="max-h-[420px] overflow-y-auto"
        // Bordure gauche 3px dans la couleur du module sur toute la
        // hauteur du bloc contenu.
        style={{ borderLeft: `3px solid ${moduleColor}` }}
      >
        {empty && (
          <div className="text-center text-[12px] text-muted-foreground py-6">
            Rien à afficher.
          </div>
        )}
        {sections.map((s) => {
          if (!(s.items || []).length) return null;
          const SecIcon = s.icon || Clock;
          return (
            <div key={s.key}>
              <div
                className="flex items-center gap-2 px-4 py-1.5 text-[10px] uppercase tracking-widest font-bold"
                style={{ background: "#1A1A1A", color: s.color || "#F87171" }}
              >
                <SecIcon size={11} />
                {s.label} · {s.items.length}{s.total && s.total > s.items.length ? ` / ${s.total}` : ""}
              </div>
              {s.items.map((it) => (
                <div
                  key={it.id}
                  className="w-full flex items-start gap-2 px-4 py-2.5 border-b border-white/[0.03] last:border-b-0"
                  data-testid={it.testid || `sidebar-popover-item-${it.id}`}
                >
                  <div className="flex-1 min-w-0">
                    {/* Ligne 1 : pastille pleine référence CLIQUABLE + priorité */}
                    <div className="flex items-center gap-2 mb-1.5">
                      {it.ref && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); nav(it.href); }}
                          className="inline-flex items-center px-2 py-0.5 rounded-md font-mono text-[10.5px] font-bold whitespace-nowrap transition-all hover:brightness-110 hover:scale-[1.03]"
                          style={{
                            background: moduleColor,
                            color: "#FFFFFF",
                            border: `1px solid ${moduleColor}`,
                          }}
                          data-testid={`sidebar-popover-ref-${it.id}`}
                          title="Ouvrir la fiche"
                        >
                          {it.ref}
                        </button>
                      )}
                      {it.priority && (
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: PRIORITY_COLOR[it.priority] || "#6B7280" }}
                          title={it.priority}
                        />
                      )}
                      {it.badge && (
                        <span className="ml-auto text-[9px] font-bold text-white px-1.5 rounded-full"
                              style={{ background: s.color }}>{it.badge}</span>
                      )}
                    </div>
                    {/* Ligne 2 : nom/objet en gras */}
                    <button
                      type="button"
                      onClick={() => nav(it.href)}
                      className="text-left w-full text-[12px] leading-snug font-semibold truncate transition-colors hover:text-white"
                      style={{ color: "#EDEDED" }}
                      title={it.title}
                    >
                      {it.title}
                    </button>
                    {/* Ligne 3 : statut (icône + libellé + date) */}
                    {(it.meta || it.statusLabel) && (
                      <div className="flex items-center gap-1.5 mt-1 text-[10.5px] text-muted-foreground">
                        <SecIcon size={10} style={{ color: s.color, flexShrink: 0 }} />
                        <span className="truncate">{it.statusLabel || it.meta}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
}

// Icon presets — utilisées par les 3 modules.
SidebarBadgePopover.Icons = {
  Waiting:  Clock,
  Unread:   MessageSquare,
  Alert:    AlertTriangle,
  Document: FileText,
  Task:     ListTodo,
};
